<template lang="pug">
  #app.location(:class="appClass")
    Preloader
    MainLayout
</template>

<script>
import 'nprogress/nprogress.css'
import '@/styles/app.sass'
import i18n from '@/plugins/i18n'
import { mapState, mapActions, mapMutations } from 'vuex'

import { SEARCH_PARAMS } from '@/constants/searchParams'
import selfHostedSites from '@/constants/selfHostedSites'

import MainLayout from '@/layouts/MainLayout'

export default {
  name: 'App',
  components: { MainLayout },
  computed: {
    ...mapState('main', ['languages', 'clientLocation', 'allowedLocales']),
    ...mapState('siteVersions', {
      versionsMap: ({ items }) => items.reduce((versionsMap, el) => ({
        ...versionsMap,
        [el.code]: el
      }), {}),
      currentSiteVersion: 'item'
    }),
    appClass: (vm) => vm.currentSiteVersion.code && `location_${vm.currentSiteVersion.code.toLowerCase()}`,
    selfHosted: () => Object.entries(selfHostedSites).find(([_, host]) => window.location.href.startsWith(host)),
    selectedLocale: (vm) => vm.$route.query[SEARCH_PARAMS.LOCALE],
    selectedLang: (vm) => vm.$route.query[SEARCH_PARAMS.LANGUAGE],
    fallbackLocale: (vm) => vm.versionsMap['global']
  },
  mounted () {
    this.boostrap()
    this.followQueryParams()
  },
  methods: {
    ...mapActions('siteVersions', {
      fetchSiteVersions: 'fetch'
    }),
    ...mapActions('feedbackSubjects', {
      fetchFeedbackSubjects: 'fetch'
    }),
    ...mapMutations('siteVersions', {
      setCurrentCountry: 'SET_ITEM'
    }),
    ...mapMutations('main', {
      setLanguages: 'SET_LANGUAGES'
    }),
    ...mapActions('main', ['getClientLocation']),
    async boostrap () {
      await this.setSiteVersion()
      await this.fetchFeedbackSubjects()
      this.setLanguage()
      this.setTheme()

      this.$nextTick(() => {
        this.$preloader.stop()
        this.vh()
        this.bodyModifier()
        this.callDSPCounter()
        this.callFBPixel()
      })
    },
    async setSiteVersion () {
      if (this.selfHosted) {
        await this.setSelfHostedVersion()
      } else {
        await this.setGlobalVersion()
      }
    },
    async setByLocation () {
      await this.getClientLocation()

      const { code } = this.versionsMap[this.clientLocation] || this.fallbackLocale

      if (selfHostedSites[code]) {
        window.location.replace(selfHostedSites[code])
      } else {
        this.$router.replace({ query: { ...this.$route.query, [SEARCH_PARAMS.LOCALE]: code } })
        location.reload()
      }
    },
    setLanguage () {
      const availableLocales = this.currentSiteVersion.locales
      const siteVersionCode = this.currentSiteVersion.code

      if (!availableLocales || !siteVersionCode) return

      let siteVersionLocale = availableLocales.find((locale) => locale === siteVersionCode) || 'en'

      const siteVersionLocales = [siteVersionLocale, ...availableLocales.filter(locale => locale !== siteVersionLocale)]

      const filteredLocales = siteVersionLocales.filter(locale => {
        const allowedList = this.allowedLocales[locale]
        if (!allowedList || !this.selectedLocale) return true
        return allowedList.includes(this.selectedLocale)
      })

      this.setLanguages(filteredLocales)

      const existsLanguage = this.languages.find(item => item.lang === this.selectedLang)

      if (!existsLanguage) {
        this.$router.replace({ query: { ...this.$route.query, [SEARCH_PARAMS.LANGUAGE]: this.languages[0].lang } })
        location.reload()
      }
    },
    async setGlobalVersion () {
      this.$i18n.locale = this.selectedLang || 'en'

      await this.fetchSiteVersions()

      const siteVersion = this.versionsMap[this.selectedLocale]

      if (siteVersion) {
        this.setCurrentCountry(siteVersion)
      } else {
        await this.setByLocation()
      }
    },
    async setSelfHostedVersion () {
      const [locale] = this.selfHosted

      this.$i18n.locale = this.selectedLang

      await this.fetchSiteVersions()

      this.setCurrentCountry(this.versionsMap[locale] || this.fallbackLocale)
    },
    setTheme () {
      document.documentElement.dataset.theme = this.selectedLocale === 'global' ? 'blue' : 'green'
    },
    vh () {
      let lastWidth = window.innerWidth

      const listener = () => {
        let vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty('--vh', `${vh}px`)
      }

      window.addEventListener('resize', () => {
        if (lastWidth !== window.innerWidth) {
          lastWidth = window.innerWidth
          listener()
        }
      })

      listener()
    },
    bodyModifier () {
      document.querySelector('body').classList.add(`locale-${i18n.locale}`)

      if (i18n.locale === 'he' || i18n.locale === 'ar') {
        document.querySelector('html').dir = 'rtl'
      }
    },
    callDSPCounter () {
      this.$dspCounter('send', {
        'site_area': 'bdvxBguO'
      })
    },
    callFBPixel () {
      this.$fbq.event('PageView')
    },
    followQueryParams () {
      if (this.$route.query[SEARCH_PARAMS.FEEDBACK]) {
        this.$modal.show('ModalFeedback')
      }
    }
  }
}
</script>
